import { createFileRoute, Link, Outlet } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { requireAuth } from "~/lib/auth-guard";
import { orpc } from "~/lib/orpc-client";
import { Skeleton } from "@alchema/ui";
import { PillTabs } from "~/components/shared/PillTabs";
import * as m from "~/paraglide/messages";

export const Route = createFileRoute("/analytics")({
  beforeLoad: async () => { await requireAuth(); },
  component: AnalyticsLayout,
  pendingComponent: AnalyticsLayoutSkeleton,
});

function AnalyticsLayoutSkeleton() {
  return (
    <div className="mx-auto max-w-4xl px-6 py-10">
      <Skeleton className="h-7 w-36 mb-1" />
      <Skeleton className="h-4 w-56 mb-8" />
      <Skeleton className="h-9 w-80 rounded-lg mb-8" />
      <div className="space-y-3">
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-20 rounded-xl" />
        ))}
      </div>
    </div>
  );
}

const TABS = [
  { to: "/analytics", label: m.analytics_tab_overview() },
  { to: "/analytics/resume-performance", label: m.analytics_tab_resume_performance() },
  { to: "/analytics/activity", label: m.analytics_tab_activity() },
];

function AnalyticsLayout() {
  const tierQuery = useQuery({
    ...orpc.billing.currentTier.queryOptions(),
    retry: 0,
  });

  if (tierQuery.isLoading) {
    return <AnalyticsLayoutSkeleton />;
  }

  const tier = tierQuery.data;
  const hasAnalytics = tier && tier.limits.analytics;

  if (!hasAnalytics) {
    return (
      <div className="mx-auto max-w-4xl px-6 py-10 animate-fade-up">
        <h1
          className="font-bold tracking-tight"
          style={{ fontFamily: "var(--font-display)", fontSize: 22, color: "var(--text)" }}
        >
          {m.analytics_title()}
        </h1>
        <p className="mt-1 text-sm" style={{ color: "var(--text-muted)" }}>
          {m.analytics_upgrade_desc()}
        </p>
        <div
          className="mt-8 rounded-xl p-8 text-center"
          style={{ border: "1px solid var(--border-subtle)", background: "var(--surface)" }}
        >
          <div className="mx-auto max-w-md">
            <h2 className="text-base font-semibold" style={{ color: "var(--text)" }}>
              {m.analytics_upgrade_title()}
            </h2>
            <p className="mt-2 text-sm" style={{ color: "var(--text-muted)" }}>
              {m.analytics_upgrade_desc()}
            </p>
            <Link
              to="/settings"
              className="mt-6 inline-block rounded-lg px-5 py-2 text-sm font-medium transition-opacity hover:opacity-80"
              style={{ background: "var(--accent)", color: "#fff" }}
            >
              {m.analytics_upgrade_cta()}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-4xl px-6 py-10 animate-fade-up">
      {/* Page header */}
      <div className="mb-6">
        <h1
          className="font-bold tracking-tight"
          style={{ fontFamily: "var(--font-display)", fontSize: 22, color: "var(--text)" }}
        >
          {m.analytics_title()}
        </h1>
        <p className="mt-1 text-sm" style={{ color: "var(--text-muted)" }}>
          {m.analytics_upgrade_desc()}
        </p>
      </div>

      <PillTabs tabs={TABS} />

      <Outlet />
    </div>
  );
}
